import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { VerificationService } from '../services/verification.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent, FeatureFlagService, OneDayInMs, Shared, UserService } from '@rubii/frontend-common';
import { ClientService } from '../services/client.service';
import { ConfigService } from '../services/config.service';
import { OtpInputComponent } from '../shared/otp-input/otp-input.component';
import { interval, Subscription } from 'rxjs';
import { TWO_FACTOR_VERIFICATION_COOKIE_NAMES, TWO_FACTOR_VERIFICATION_MESSAGES, TwoFactorEvents, twoFAFeatureId } from './two-factor-verification.constants';
import { VerifyMobileResponse } from '@rubii/sdk-web-user/src/types';

@Component({
    selector: 'app-verification',
    templateUrl: './verification.component.html',
    styleUrls: ['./verification.component.scss']
})
export class VerificationComponent extends BaseComponent implements OnInit, OnDestroy {
    @ViewChildren(OtpInputComponent) otpInputs: QueryList<OtpInputComponent>;
    verifyType: "mobile" | "email" = "email";
    verifyEmailLinkToken = '';

    errorType: 'invalid' | 'locked' | 'paused' | 'incomplete' | 'expired' | '' = '';

    otpValues: string[] = ['', '', '', '', '', ''];
    mobileNumber: string = '';
    maskedMobileNumber: string = '';
    isVerifying = false;
    verifySuccess = false;
    shouldResendOtp = false;
    // pause state (timestamp in ms)
    pausedUntil = 0;
    isPaused = false;
    pauseRemainingSeconds = 0;
    showVerifyMsg = false;
    verifyMsg = '';
    submitDisable = false;
    resendDisabled: boolean = false;
    countdown: number = 0;
    resendEmailCountdown: number = 0;
    isLocked: boolean = false;
    emailResendInProgress = false;
    isMobileVerified: boolean = false;
    twoFactorSettings = {
        'otp_max_attempts': 3,
        'otp_expiry_seconds': 600,
        'verify_otp_generate_pause_seconds': 900,
        'email_max_attempts': 3,
        'email_expiry_seconds': 900,
        'email_send_pause_seconds': 900,
    };

    private pauseSubs: { [k: string]: Subscription } = {};
    constructor(
        private verificationService: VerificationService,
        private snack: MatSnackBar,
        private route: ActivatedRoute,
        private router: Router,
        private client: ClientService,
        private config: ConfigService,
        private userService: UserService,
        private feature: FeatureFlagService) {
        super();
        let verficationEmailLink = router.url.toLowerCase().indexOf("verificationlink") >= 0;
        if (verficationEmailLink) {
            this.verifyEmailLinkToken = route.snapshot.paramMap.get("token");
            this.verifyEmailLink();
        }
        let isLocked = router.url.toLowerCase().indexOf("lock") >= 0;
        if (isLocked) {
            this.errorType = 'locked';
        }
        this.mobileNumber = this.userService.user.mobile;
        if (this.mobileNumber) {
            const digits = this.mobileNumber.replace(/\D/g, '');
            const lastFour = digits.slice(-4);
            this.maskedMobileNumber = digits.length > 4 ? '*'.repeat(digits.length - 4) + lastFour : lastFour;
        }
        this.getTwoFactorSettings();
    }

    ngOnInit(): void {
        this.verifyType = this.verificationService.verifyType;
        document.title = 'Two Factor Verification | Rubii - Digital Media Trading';

        this.isMobileVerified = this.userService.user?.mobileVerified === 1;
        if (this.verifyType === 'mobile') {
            const otpLastAttempt = Shared.helpers.CookieHelper.getCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_LAST_ATTEMPT);
            if (otpLastAttempt) {
                const lastAttempt = JSON.parse(otpLastAttempt).lastAttempt;
                const timeElapsed = Date.now() - lastAttempt;
                if (timeElapsed < 60000) { // Less than 1 minutes
                    this.resendDisabled = true;
                    this.countdown = Math.ceil((60000 - timeElapsed) / 1000);
                    this.countdown = Math.max(1, this.countdown);
                    this.startPauseCountdown(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_LAST_ATTEMPT, () => {
                        this.resendDisabled = false;
                    });
                } else {
                    Shared.helpers.CookieHelper.deleteCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_LAST_ATTEMPT);
                }
            }

            const otpPaused = Shared.helpers.CookieHelper.getCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_GENERATION_PAUSED);
            if (otpPaused) {
                const pausedData = JSON.parse(otpPaused);
                const timeElapsed = Date.now() - pausedData.lastAttempt;
                if (timeElapsed < this.twoFactorSettings['verify_otp_generate_pause_seconds'] * 1000) {
                    this.errorType = 'incomplete';
                    this.countdown = Math.ceil((this.twoFactorSettings['verify_otp_generate_pause_seconds'] * 1000 - timeElapsed) / 1000);
                    this.countdown = Math.max(1, this.countdown);
                    this.startPauseCountdown(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_GENERATION_PAUSED, () => {
                        this.router.navigate(['/login']);
                    });
                } else {
                    Shared.helpers.CookieHelper.deleteCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_GENERATION_PAUSED);
                }
            }
        } else if (this.verifyType === 'email') {
            const emailLastAttempt = Shared.helpers.CookieHelper.getCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_LAST_ATTEMPT);
            if (emailLastAttempt) {
                const lastAttempt = JSON.parse(emailLastAttempt).lastAttempt;
                const timeElapsed = Date.now() - lastAttempt;
                const COOLDOWN_MS = 60000;
                if (timeElapsed < COOLDOWN_MS) {
                    this.resendEmailCountdown = Math.ceil((COOLDOWN_MS - timeElapsed) / 1000);
                    this.resendEmailCountdown = Math.max(1, this.resendEmailCountdown);
                    this.startResendEmailCooldown(lastAttempt);
                } else {
                    Shared.helpers.CookieHelper.deleteCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_LAST_ATTEMPT);
                }
            }

            const emailPaused = Shared.helpers.CookieHelper.getCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_VERIFICATION_PAUSED);
            if (emailPaused) {
                const pausedData = JSON.parse(emailPaused);
                const timeElapsed = Date.now() - pausedData.lastAttempt;
                if (timeElapsed < this.twoFactorSettings['email_send_pause_seconds'] * 1000) { // Less than 15 minutes
                    this.errorType = 'paused';
                    this.countdown = Math.ceil((this.twoFactorSettings['email_send_pause_seconds'] * 1000 - timeElapsed) / 1000);
                    this.countdown = Math.max(1, this.countdown);
                    this.startPauseCountdown(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_VERIFICATION_PAUSED, () => {
                        this.router.navigate(['/login']);
                    });
                } else {
                    Shared.helpers.CookieHelper.deleteCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_VERIFICATION_PAUSED);
                }
            }
        }
    }

    onValueChange(data: { value: string, index: number }): void {
        this.otpValues[data.index] = data.value;
        this.otpValues = [...this.otpValues];
        if (this.otpValues.every(val => val.length === 1)) {
            setTimeout(() => {
                this.onSubmit();
            }, 100);
        }
    }

    onMoveNext(index: number): void {
        if (index < 5) {
            const nextInput = this.otpInputs.toArray()[index + 1];
            if (nextInput) {
                nextInput.focus();
            }
        }
    }

    onMovePrevious(index: number): void {
        if (index > 0) {
            const prevInput = this.otpInputs.toArray()[index - 1];
            if (prevInput) {
                prevInput.focus();
            }
        }
    }

    onOtpPaste(event: ClipboardEvent): void {
        const pastedData = event.clipboardData?.getData('text');
        if (pastedData && /^\d{6}$/.test(pastedData)) {
            for (let i = 0; i < 6; i++) {
                this.otpValues[i] = pastedData[i];
            }
            this.otpValues = [...this.otpValues];

            setTimeout(() => {
                this.onSubmit();
            }, 100);
        }
    }

    onCancel(): void {
    }

    onChangeToUsingMobile() {
        this.verificationService.verifyType = 'mobile';
        this.verifyType = 'mobile';
        this.onResendOtp();
    }

    onResendEmail() {
        if (this.emailResendInProgress || this.resendEmailCountdown > 0) {
            this.openSnackBarWarning(this.snack, `Please wait ${this.resendEmailCountdown} second(s) before resending the verification email.`, 'close');
            return;
        }
        this.emailResendInProgress = true;
        const lastAttempt = Date.now();
        Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_LAST_ATTEMPT, JSON.stringify({ lastAttempt }));
        this.startResendEmailCooldown(lastAttempt);

        const sendEmailParams = {
            userId: this.userService.user.id,
            email: this.userService.user.email,
        };
        this.client.user.sendVerificationEmail(sendEmailParams).then((res) => {
            this.emailResendInProgress = false;
            if (res && res.result) {
                this.openSnackBar(this.snack, 'Verification email has been resent successfully', 'close');
            } else if (res && res.message.toLowerCase().includes(TWO_FACTOR_VERIFICATION_MESSAGES.VERIFICATION_PAUSED)) {
                const lastAttempt = res.lastAttempt || Date.now();
                res.lastAttempt = lastAttempt;
                Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_VERIFICATION_PAUSED, JSON.stringify(res));
                this.errorType = 'paused';
                const timeElapsed = Date.now() - res.lastAttempt;
                this.countdown = Math.ceil((this.twoFactorSettings['email_send_pause_seconds'] * 1000 - timeElapsed) / 1000); // Calculate remaining time
                this.countdown = Math.max(1, this.countdown);
                this.startPauseCountdown(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_VERIFICATION_PAUSED, () => {
                    this.router.navigate(['/login']);
                });
            } else {
                this.openSnackBarWarning(this.snack, res.message || 'Failed to resend verification email. Please try again later.', 'close');
            }
        }).catch(() => {
            this.emailResendInProgress = false;
            this.openSnackBarWarning(this.snack, 'An error occurred while resending verification email. Please try again later.', 'close');
        });
    }

    onResendOtp(): void {
        if (this.resendDisabled || this.countdown > 0) {
            this.openSnackBarWarning(this.snack, `Please wait ${this.countdown} second(s) before resending the OTP.`, 'close');
            return;
        }
        const usermobile = this.userService.user.mobile;
        const cleanedMobile = usermobile.replace(/\D/g, '');
        const countryCode = +cleanedMobile.slice(0, 2);
        const phoneNumber = +cleanedMobile.slice(2);

        this.client.user.loginWithSendMobileCode({
            countryCode: countryCode,
            phoneNumber: phoneNumber,
        }).then((res) => {
            if (res && res.result) {
                this.openSnackBar(this.snack, 'OTP has been resent successfully', 'close');
                this.submitDisable = false;
                this.otpValues = ['', '', '', '', '', ''];
                this.otpValues = [...this.otpValues];
                const firstInput = this.otpInputs.toArray()[0];
                if (firstInput) {
                    firstInput.focus();
                }
                Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_LAST_ATTEMPT, JSON.stringify(res));
                const timeElapsed = Date.now() - res.lastAttempt;
                if (timeElapsed < 60000) { 
                    this.resendDisabled = true;
                    this.countdown = Math.ceil((60000 - timeElapsed) / 1000);
                    this.countdown = Math.max(1, this.countdown);
                    this.startPauseCountdown(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_LAST_ATTEMPT, () => {
                        this.resendDisabled = false;
                    });
                } else {
                    Shared.helpers.CookieHelper.deleteCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_LAST_ATTEMPT);
                }
            } else if (res && res.message.toLowerCase().includes(TWO_FACTOR_VERIFICATION_MESSAGES.VERIFICATION_INCOMPLETE)) {
                this.errorType = 'incomplete';
                const lastAttempt = res.lastAttempt || Date.now(); 
                res.lastAttempt = lastAttempt;
                const timeElapsed = Date.now() - res.lastAttempt;
                this.countdown = Math.ceil((this.twoFactorSettings['verify_otp_generate_pause_seconds'] * 1000 - timeElapsed) / 1000);
                this.countdown = Math.max(1, this.countdown);

                Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_GENERATION_PAUSED, JSON.stringify(res));

                this.startPauseCountdown(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_GENERATION_PAUSED, () => {
                    this.router.navigate(['/login']);
                });
            } else {
                this.openSnackBarWarning(this.snack, 'Failed to resend OTP. Please try again later.', 'close');
            }
        }).catch(() => {
            this.openSnackBarWarning(this.snack, 'An error occurred while resending OTP. Please try again later.', 'close');
        });
    }

    async onSubmit(): Promise<void> {
        if (this.isLocked) {
            return;
        }

        const otp = this.otpValues.join('');

        if (otp.length !== 6) {
            this.openSnackBarWarning(this.snack, 'Please enter the 6-digit OTP', 'close');
            return;
        }
        this.isVerifying = true;
        this.showVerifyMsg = false; 

        const cleanedMobile = this.userService.user.mobile.replace(/\D/g, '');
        const countryCode = +cleanedMobile.slice(0, 2);
        const phoneNumber = +cleanedMobile.slice(2);

        let res: VerifyMobileResponse;
        try {
            res = await this.client.user.loginWithMobileVerify({
                countryCode: countryCode,
                phoneNumber: phoneNumber,
                verifyCode: +otp
            });
        } catch (err) {
            console.error('Error during mobile verification:', err);
            this.isVerifying = false;
            this.openSnackBarWarning(this.snack, 'Verification failed. Please try again.', 'close');
            return;
        }

        if (res && res.result) {
            await this.triggerTwoFAEvent(TwoFactorEvents.TwoFAPassedByMobile, {email: this.userService.user?.email, mobile: this.userService.user?.mobile});
            this.openSnackBar(this.snack, 'Verification successful', 'close');
            this.setTwoFactorCookie(res);
            this.deleteVerificationCookies();
            this.verificationService.gotoRedirectUrl();
        } else {
            await this.triggerTwoFAEvent(TwoFactorEvents.TwoFAFailedByMobile, {email: this.userService.user?.email, mobile: this.userService.user?.mobile, failedMsg: res?.message || ''});
            if (res && res.message.toLowerCase().includes(TWO_FACTOR_VERIFICATION_MESSAGES.OTP_EXPIRED)) {
                this.isVerifying = false;
                this.submitDisable = true;
                this.openSnackBarWarning(this.snack, 'OTP expired. Please request a new one.', 'close');
                return;
            } else if (res && res.message.toLowerCase().includes(TWO_FACTOR_VERIFICATION_MESSAGES.OTP_INCORRECT)) {
                this.isVerifying = false;
                this.openSnackBarWarning(this.snack, res.message, 'close');
                return;
            } else if (res && res.message.toLowerCase().includes(TWO_FACTOR_VERIFICATION_MESSAGES.OTP_LOCKED)) {
                await this.triggerTwoFAEvent(TwoFactorEvents.UserLocked, { email: this.userService.user?.email, mobile: this.userService.user?.mobile, failedMsg: res?.message || '' });
                this.errorType = 'locked';
                this.isLocked = true;
                const lastAttempt = res.lastAttempt || Date.now(); 
                const lockData = { lockedAt: lastAttempt };
                Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.USER_LOCKED, JSON.stringify(lockData));
                return;
            } else if (res && res.message.toLowerCase().includes(TWO_FACTOR_VERIFICATION_MESSAGES.VERIFICATION_INCOMPLETE)) {
                this.errorType = 'incomplete';
                const lastAttempt = res.lastAttempt || Date.now(); 
                res.lastAttempt = lastAttempt;
                const timeElapsed = Date.now() - res.lastAttempt;
                this.countdown = Math.ceil((this.twoFactorSettings['verify_otp_generate_pause_seconds'] * 1000 - timeElapsed) / 1000);
                this.countdown = Math.max(1, this.countdown);

                Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_GENERATION_PAUSED, JSON.stringify(res));

                this.startPauseCountdown(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_GENERATION_PAUSED, () => {
                    this.router.navigate(['/login']);
                });
                return;
            } else {
                this.openSnackBarWarning(this.snack, res.message || 'verification failed', 'close');
                return;
            }
        }
    }

    isSubmitDisabled(): boolean {
        return !this.otpValues.every(val => val.length === 1);
    }

    trackByIndex(index: number): number {
        return index;
    }

    backToLogin() {
        this.router.navigate(['/login']);
    }

    private verifyEmailLink() {
        this.client.user.handleEmailVerification({ token: this.verifyEmailLinkToken }).then((res) => {
            this.errorType = '';
            if (res.result) {
                this.triggerTwoFAEvent(TwoFactorEvents.TwoFAPassedByEmail, {email: this.userService.user?.email});
                this.setTwoFactorCookie(res);
                this.deleteVerificationCookies();
                this.verificationService.gotoRedirectUrl();
            } else if (!res.result) {
                this.triggerTwoFAEvent(TwoFactorEvents.TwoFAFailedByEmail, {email: this.userService.user?.email, failedMsg: res?.message || ''});
                if (res.message) {
                    const toLowerCasedMessage = res.message.toLowerCase();
                    if (toLowerCasedMessage.includes(TWO_FACTOR_VERIFICATION_MESSAGES.EMAIL_EXPIRE)) {
                        this.errorType = 'expired';
                    } else if (toLowerCasedMessage.includes(TWO_FACTOR_VERIFICATION_MESSAGES.USER_LOCKED)) {
                        this.errorType = 'locked';
                    } else if (toLowerCasedMessage.includes(TWO_FACTOR_VERIFICATION_MESSAGES.VERIFICATION_PAUSED)) {
                        this.errorType = 'paused';
                    } else {
                        this.openSnackBarWarning(this.snack, res.message, 'close');
                    }
                }
            }
        }).catch((err) => {
        });
    }

   
    private async triggerTwoFAEvent(event: TwoFactorEvents, payload?: any): Promise<void> {
        try {
            await this.feature.trigger(twoFAFeatureId, event, payload || {});
        } catch (e) {
            console.error('Failed to trigger feature event', event, e);
        }
    }

    private deleteVerificationCookies() {
        Shared.helpers.CookieHelper.deleteCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_VERIFICATION_PAUSED);
        Shared.helpers.CookieHelper.deleteCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_GENERATION_PAUSED);
        Shared.helpers.CookieHelper.deleteCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.USER_LOCKED);
        Shared.helpers.CookieHelper.deleteCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.OTP_LAST_ATTEMPT);
    }

    /**
     * Start a countdown based on this.countdown and delete the given cookie when finished.
     * Ensures countdown is at least 1 second before starting.
     * onComplete is invoked when countdown finishes (before cookie deletion).
     */
    private startResendEmailCooldown(lastAttempt?: number): void {
        const COOLDOWN_KEY = `__resendEmail_${this.userService.user?.email}`;
        const COOLDOWN_MS = 60000;
        if (this.pauseSubs[COOLDOWN_KEY]) {
            this.pauseSubs[COOLDOWN_KEY].unsubscribe();
            delete this.pauseSubs[COOLDOWN_KEY];
        }
        if (lastAttempt !== undefined) {
            const elapsed = Date.now() - lastAttempt;
            this.resendEmailCountdown = Math.max(0, Math.ceil((COOLDOWN_MS - elapsed) / 1000));
        } else {
            this.resendEmailCountdown = Math.ceil(COOLDOWN_MS / 1000);
        }
        if (this.resendEmailCountdown <= 0) {
            Shared.helpers.CookieHelper.deleteCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_LAST_ATTEMPT);
            return;
        }
        const sub = interval(1000).subscribe(() => {
            this.resendEmailCountdown--;
            if (this.resendEmailCountdown <= 0) {
                sub.unsubscribe();
                delete this.pauseSubs[COOLDOWN_KEY];
                Shared.helpers.CookieHelper.deleteCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.EMAIL_LAST_ATTEMPT);
            }
        });
        this.pauseSubs[COOLDOWN_KEY] = sub;
    }

    private startPauseCountdown(cookieName: string, onComplete?: () => void): Subscription {
        this.countdown = Math.max(1, Math.ceil(this.countdown));
        if (this.pauseSubs[cookieName]) {
            this.pauseSubs[cookieName].unsubscribe();
            delete this.pauseSubs[cookieName];
        }
        const sub = interval(1000).subscribe(() => {
            this.countdown--;
            if (this.countdown <= 0) {
                sub.unsubscribe();
                Shared.helpers.CookieHelper.deleteCookie(cookieName);
                if (onComplete) {
                    try { onComplete(); } catch (e) { console.log(e); }
                }
            }
        });
        this.pauseSubs[cookieName] = sub;
        return sub;
    }

    private getTwoFactorSettings() {
        this.client.user.getTwoFactorSettings().then((res: Object) => {
            if (res) {
                this.twoFactorSettings = {
                    ...this.twoFactorSettings,
                    ...res
                };
            } else {
                console.warn('Invalid settings response:', res);
            }
        }).catch((err) => {
            // Handle any errors
            console.error('Error fetching two-factor settings:', err);
        });
    }

    private setTwoFactorCookie(cookieData: any) {
        const useremail = this.userService.user.email;
        const trustDeviceChecked = Shared.helpers.CookieHelper.getCookie(`${TWO_FACTOR_VERIFICATION_COOKIE_NAMES.TWO_FACTOR_TRUSTED_DEVICE_CHECKED}-${useremail}`);
        Shared.helpers.CookieHelper.setCookie(TWO_FACTOR_VERIFICATION_COOKIE_NAMES.TWO_FACTOR_PASSED, JSON.stringify(cookieData));
        if (trustDeviceChecked === "true") {
            this.verificationService.setCookieWithExpire(`${TWO_FACTOR_VERIFICATION_COOKIE_NAMES.TWO_FACTOR_TRUSTED_DEVICE}-${useremail}`, "true", 30 * OneDayInMs);
        }
    }

    ngOnDestroy(): void {
        if (this.pauseSubs) {
            Object.values(this.pauseSubs).forEach(s => { try { s.unsubscribe(); } catch (e) { } });
        }
        this.pauseSubs = {};
    }
}